import { Capacitor } from '@capacitor/core'
import {
    ColoursObj,
    TextInputField,
    Typography,
} from '@eventr/common-storybook'
import { Box, Stack } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { HeaderSearchBar } from '../../home/components/carousel/slide/footer/HeaderSearchBar'
import i18n from '../../i18n/i18next'
import { groupChatName } from '../../state/rocketchatState/rocketchatSignals'
import { useScrollContext } from '../../state/scrollState/scrollContext'
import {
    keyboardState,
    windowSizes,
} from '../../state/windowState/windowSignals'
import { navBarHidden, navBarRef } from './navBar'
import { ToolbarItem, ToolbarItemProps } from './TopToolbar'

interface NavigationViewProps {
    // The contents of the NavigationView.
    children: React.ReactNode

    // The title of the navigation view, displayed in the navigation bar with
    // bold styling.
    navigationBarTitle?: string | React.ReactNode

    // The subtitle of the navigation view, displayed in the navigation bar with
    // subtext styling.
    navigationBarSubtitle?: string | React.ReactNode

    // The navigation bar display mode. When inline, the title begins inline
    // with the navigation bar toolbar items (e.g., a back button). When large,
    // the navigation title begins below the back button, and the inline version
    // fades in when the large title is scrolled past and no longer visible.
    navigationBarDisplayMode?: 'inline' | 'large'

    // If nil, no back button is displayed. If not nil, then a button is
    // displayed as the first item in the leading navigation bar, styled with a
    // chevron pointing to the left. When pressed, this changes the page
    // transitions to use transitions specific to going back in the navigation
    // hierarchy. As a result, actions should always exit the current
    // navigation view.
    backButtonAction?: () => void

    // The items to render in the leading navigation bar. Note that if a back
    // button action is provided, then the back button will be the first leading
    // item.
    navigationBarLeading?: ToolbarItemProps[]

    // The items to render in the trailing navigation bar.
    navigationBarTrailing?: ToolbarItemProps[]

    // Whether to animate entry and exit using framer-motion, with support for
    // changing transitions based on whether the view is going up or down the
    // navigation hierarchy.
    usePageTransition?: boolean

    // Whether the navigation view is the root of the navigation hierarchy. When
    // the view is the root view, there is no entry animation. Note that there
    // is an entry animation when a view lower in the hierarchy returns to the
    // root, to ensure consistent behaviour.
    isRoot?: boolean

    // Unused property. Designed to present buttons in a floating container.
    footer?: React.ReactNode

    tag?: string

    /// I dont have time to test all pages so only changing the page that i care about.
    /// Needs to be sorted properly...
    removeTopPadding?: boolean

    //back button custom react node
    backButton?: ToolbarItemProps

    /// Added for booking hotel page image under header
    pictureMode?: boolean

    profilePage?: boolean

    count?: number

    groupNameField?: boolean

    userSearchBar?: boolean

    stickyUnderlay?: React.ReactNode

    autoHeaderPadding?: boolean
}

// navBarRef?.value?.current?.offsetHeight already has windowSizes.value.insets.insets.bottom in it if displayed
// only if  navBarRef?.value?.current?.offsetHeight == 0 we need to use windowSizes.value.insets.insets.bottom
const footerDeadZone = () => {
    if (Capacitor.isNativePlatform()) {
        if (['didShow', 'willShow'].includes(keyboardState.value.openState)) {
            return '0px'
        }
        if (navBarRef?.value?.current?.offsetHeight == 0) {
            return windowSizes.value.insets.insets.bottom + 'px'
        }
        return (navBarRef?.value?.current?.offsetHeight ?? 0) + 'px'
    } else {
        /// Webview no deadzone
        return (navBarRef?.value?.current?.offsetHeight ?? 0) + 'px'
    }
}

export const NavigationView = ({
    children,
    profilePage,
    navigationBarTitle,
    navigationBarSubtitle,
    navigationBarDisplayMode,
    backButtonAction,
    navigationBarLeading,
    navigationBarTrailing,
    usePageTransition,
    isRoot,
    footer,
    tag,
    removeTopPadding = false,
    backButton,
    pictureMode = false,
    count = 0,
    userSearchBar = false,
    groupNameField = false,
    stickyUnderlay,
    autoHeaderPadding = false,
}: NavigationViewProps) => {
    // Holds the scroll offset, which is used to present and hide the shadow on
    // the navigation bar. Also holds the useBackAnimation property, which is
    // toggled to true when a back button is pressed during the duration of the
    // animation. This ensures that other navigation views that are returned to
    // accordingly use the correct entry animation.
    const [atTop, setAtTop] = useState(useScrollContext.getState().atTop)

    const viewHeaderRef = useRef<HTMLDivElement>(null)
    // Connect to the store on mount, disconnect on unmount, catch state-changes in a reference
    useEffect(
        () => useScrollContext.subscribe((state) => setAtTop(state.atTop)),
        [setAtTop]
    )

    const [groupName, setGroupName] = useState<string>()

    useEffect(() => {
        if (userSearchBar) {
            groupChatName.value = groupName
        }
    }, [userSearchBar, groupName])

    const [height, setHeight] = useState(0)
    const footerRef = useRef(null)

    useEffect(() => {
        if (navBarHidden.value) {
            setHeight(footerRef?.current?.clientHeight ?? 0)
            return
        }

        setHeight((footerRef?.current?.clientHeight ?? 0) + 70)
    }, [navBarRef.value, navBarHidden.value])

    const titleLength =
        typeof navigationBarTitle === 'string' ? navigationBarTitle.length : 0

    // The title typography.
    const titleTypography = navigationBarTitle && (
        <Typography
            variant={titleLength > 20 ? 'bodyBold' : 'mobileH3'}
            textAlign="center"
            margin="auto"
            width={'100%'}
            id={String(navigationBarTitle)}
            display={'-webkit-box'}
            style={{
                lineClamp: 2,
                boxOrient: 'vertical',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
            }}
            overflow="hidden"
            textOverflow="ellipsis"
            // whiteSpace="nowrap"
        >
            {navigationBarTitle}
        </Typography>
    )

    // The subtitle typography.
    const subtitleTypography = navigationBarSubtitle && (
        <Typography
            id={String(navigationBarSubtitle)}
            variant="bodySubtext"
            textAlign="center"
            width="100%"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            color={ColoursObj.core.primary}
        >
            {navigationBarSubtitle}
        </Typography>
    )

    // The back button. Carries out the back button action if provided, toggled
    // use back animation to true before.
    const backButtonDefault: ToolbarItemProps = {
        id: 'backButtonNavigation',
        icon: 'Chevron_Left',
        action: () => {
            if (backButtonAction) {
                backButtonAction()
            }
        },
    }

    const topPadding = () => {
        if (pictureMode) return '0px'
        if (removeTopPadding) return ''
        if (autoHeaderPadding) return viewHeaderRef.current?.offsetHeight + 'px'
        return '64px'
    }
    // The body of the navigation view, containing the navigation title if the
    // display mode is set to 'large'.
    const body = (
        <Stack
            height="100%"
            width="100vw"
            bgcolor={ColoursObj.backgrounds.primary}
        >
            <Stack paddingTop={topPadding}>
                {navigationBarDisplayMode === 'large' && (
                    <Stack padding="16px" spacing="8px">
                        <Stack direction="row" alignItems="center">
                            {typeof navigationBarTitle === 'string' && (
                                <Typography
                                    variant="mobileH2"
                                    color={ColoursObj.core.primary}
                                    id={String(navigationBarTitle)}
                                >
                                    {navigationBarTitle}
                                </Typography>
                            )}
                            {typeof navigationBarTitle !== 'string' && (
                                <>{navigationBarTitle}</>
                            )}
                            {tag && (
                                <div style={{ paddingLeft: '8px' }}>
                                    <div
                                        style={{
                                            background: `linear-gradient(45deg, ${ColoursObj.core.primary}, ${ColoursObj.core.secondary})`,
                                            color: ColoursObj.backgrounds
                                                .primary,
                                            fontFamily: 'Inter-600-900',
                                            fontWeight: '600',
                                            padding: '4px 8px',
                                            borderRadius: '16px',
                                        }}
                                    >
                                        {tag}
                                    </div>
                                </div>
                            )}
                        </Stack>

                        <Typography
                            variant="bodyRegular"
                            color={ColoursObj.core.primary}
                            id={String(navigationBarSubtitle)}
                        >
                            {navigationBarSubtitle}
                        </Typography>
                    </Stack>
                )}

                {children}
                {/*  this div is a spacer for footer*/}
                <div style={{ height: height }} />
            </Stack>
        </Stack>
    )

    // The navigation bar, containing leading and trailing toolbar items and the
    // title and subtitle typography. Presents with a shadow if the scroll
    // offset is none zero.
    const navigationBar = (
        <Stack
            id="NavigationViewHeader"
            ref={viewHeaderRef}
            paddingX="16px"
            bgcolor={
                pictureMode ? 'transparent' : ColoursObj.backgrounds.primary /// idk why we have a bg colour here, why not allways trans?
            }
            paddingBottom="8px"
        >
            <Stack
                direction="row"
                height={removeTopPadding ? '' : '56px'}
                justifyContent="space-between"
                alignItems="center"
            >
                <Stack
                    direction="row"
                    gap="8px"
                    minWidth="70px"
                    justifyContent="start"
                >
                    {/* Added so that back button wont appear if not given a function. Also should not do anything if not given a function. */}
                    <Box>
                        {backButtonAction && (
                            <ToolbarItem
                                props={backButton ?? backButtonDefault}
                            />
                        )}
                    </Box>

                    {navigationBarLeading &&
                        navigationBarLeading.map((toolbarItem, index) => {
                            return (
                                <div key={index}>
                                    <ToolbarItem props={toolbarItem} />
                                </div>
                            )
                        })}
                </Stack>

                {navigationBarDisplayMode !== 'large' && (
                    <Stack overflow="hidden" gap="4px">
                        {titleTypography}

                        {subtitleTypography}
                    </Stack>
                )}

                <Stack
                    direction="row"
                    gap="8px"
                    maxWidth="70px"
                    minWidth="70px"
                    justifyContent="end"
                >
                    {navigationBarTrailing &&
                        navigationBarTrailing.map((toolbarItem, index) => {
                            return (
                                <div key={index}>
                                    {!!count && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                display: 'grid',
                                                backgroundColor:
                                                    ColoursObj.states.error,
                                                borderRadius: '50%',
                                                height: '16px',
                                                width: '16px',
                                                justifyItems: 'center',
                                                alignItems: 'center',
                                                translate: '25px -5px',
                                            }}
                                        >
                                            <Typography
                                                color={ColoursObj.shades.shade5}
                                                variant="bodySubtext"
                                                fontSize={
                                                    count > 9 ? '8px' : '12px'
                                                }
                                                lineHeight={
                                                    count > 9 ? '8px' : '12px'
                                                }
                                            >
                                                {count}
                                            </Typography>
                                        </Box>
                                    )}

                                    <ToolbarItem
                                        props={{
                                            ...toolbarItem,
                                            profilePage,
                                        }}
                                    />
                                </div>
                            )
                        })}
                </Stack>
            </Stack>
            <Stack>
                {groupNameField && (
                    <TextInputField
                        value={groupName}
                        type="text"
                        fieldLabel={i18n.t('rocketChat.groupName')}
                        onChange={(event) => {
                            setGroupName(event.currentTarget.value)
                        }}
                    />
                )}
                {userSearchBar && <HeaderSearchBar />}
            </Stack>
            {stickyUnderlay && stickyUnderlay}

            <Box></Box>
        </Stack>
    )

    // The content to display wrapped in a Page Transition.
    const withPageTransition = (
        <div>
            <PageTransition rootZIndex={60}>
                <div
                    style={{
                        boxShadow: pictureMode
                            ? ''
                            : !atTop &&
                              '0px 4px 8px 0px rgba(51, 82, 111, 0.1)',
                        transition: '0.5s',
                    }}
                >
                    {navigationBar}
                </div>
            </PageTransition>

            {/* <PageTransition
                rootZIndex={50}
                animatePosition="static"
                animateEntry={isRoot ? false : true} */}
            {/* > */}
            <div
                className="content"
                style={{ paddingTop: userSearchBar ? '105px' : '' }}
            >
                {body}
            </div>
            {/* </PageTransition> */}

            {/* <PageTransitionFooter
                rootZIndex={55}
                animatePosition="fixed"
                animateEntry={isRoot ? false : true}
            > */}
            <div
                id="NavigationViewFooter"
                style={{
                    position: 'fixed',
                    bottom: footerDeadZone(),
                    width: '100%',
                    backgroundColor: ColoursObj.backgrounds.primary,
                }}
                ref={footerRef}
            >
                {footer}
            </div>
            {/* </PageTransitionFooter> */}
        </div>
    )

    // The content to display with no Page Transition wrapped.
    const withoutPageTransition = (
        <>
            <Stack>
                <div
                    style={{
                        boxShadow:
                            !atTop && '0px 4px 8px 0px rgba(51, 82, 111, 0.1)',
                        transition: '0.5s',
                        position: 'fixed',
                        width: '100%',
                        zIndex: 600,
                    }}
                >
                    {navigationBar}
                </div>

                {body}
                <div
                    id="NavigationViewFooter"
                    style={{
                        position: 'fixed',
                        bottom: footerDeadZone(),
                        width: '100%',
                        backgroundColor: ColoursObj.backgrounds.primary,
                    }}
                    ref={footerRef}
                >
                    {footer}
                </div>
            </Stack>
        </>
    )

    // Returns the appropriate content dependent on the usePageTransition
    // property.
    return usePageTransition !== undefined
        ? usePageTransition
            ? withPageTransition
            : withoutPageTransition
        : withoutPageTransition
}

// A wrapper to apply page transition animations, designed to be used with
// Navigation View.
const PageTransition = ({ children, rootZIndex }) => {
    return (
        <div
            className="header-animation"
            style={{
                zIndex: rootZIndex,
                width: '100vw',
                position: 'fixed',
            }}
        >
            {children}
        </div>
    )
}
